/**
 * lib/assembleReport.ts
 * ------------------------------------------------------------------
 * Turns raw Firestore marks + comments into render-ready
 * ReportStudent objects, applying the term-aware engine so the PDF,
 * the on-screen preview, and the editor edit-view all agree.
 * ------------------------------------------------------------------
 */
import {
  collection,
  getDocs,
  query,
  where,
  doc,
  getDoc,
} from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { COLLECTIONS } from '@/contexts/DataContext';
import { deriveMark, schemeForSubject, termUsesExam } from '@/lib/grading';
import { isJuneCohort } from '@/lib/cohort';
import { computeClassAttendance } from '@/lib/attendanceCalc';
import { resolveTermRange } from '@/lib/termDates';
import type { ReportStudent, ReportSubjectRow } from '@/lib/reportPdf';
import type {
  MarksDocument,
  ReportCommentsDocument,
  DailyAttendanceDoc,
  Student,
  Subject,
  Teacher,
  SchoolClass,
  AppUser,
  MaybeMark,
  TermCalendarDocument,
} from '@/types';

export interface AssembleArgs {
  year: string;
  term: string;
  classId: string;
  classes: SchoolClass[];
  students: Student[];
  subjects: Subject[];
  teachers: Teacher[];
  /** For resolving comment-author (Class Teacher / HOY) NAMES onto the report. */
  users?: AppUser[];
  /** Optional editor comment overrides applied on top (feature #3). */
  commentsDoc?: ReportCommentsDocument | null;
  /** Term calendar — scopes daily attendance to the term's date range. */
  termCalendar?: TermCalendarDocument | null;
}

/**
 * Does this subject belong on the pupil's report?
 *
 * A report's subject list comes from the MARKS, not from the pupil's subject
 * list — this file includes a subject if the marks document has an entry keyed
 * by that pupil. So a pupil who drops a subject keeps printing it if the
 * teacher left an entry behind, which is how 111 stale entries accumulated,
 * most of them a pupil marked absent rather than taken off the subject.
 *
 * The rule is BOTH conditions, and the second matters as much as the first:
 * the pupil is not mapped to the subject AND nobody has written anything about
 * them in it.
 *
 * AN ORPHAN THAT HAS A COMMENT STILL PRINTS. A teacher who wrote about a pupil
 * taught that pupil, so the likelihood is that the SUBJECT LIST is wrong
 * rather than the mark — which is exactly what happened with one pupil's
 * Chinese, and it was caught only because the subject printed. Hiding it would
 * turn a wrong subject list into an error nobody ever sees; a missing subject
 * is worse than a surplus one, because nobody queries what is not there.
 *
 * A mapped pupil awaiting a comment is untouched.
 */
export function subjectBelongsOnReport({
  takesSubject,
  comment,
}: {
  takesSubject: boolean;
  comment?: string;
}): boolean {
  if (takesSubject) return true;
  return (comment ?? '').trim().length > 0;
}

export async function assembleClassReport({
  year,
  term,
  classId,
  classes,
  students,
  subjects,
  teachers,
  users,
  commentsDoc,
  termCalendar,
}: AssembleArgs): Promise<ReportStudent[]> {
  const cls = classes.find((c) => c.id === classId);
  const className = cls?.name ?? classId;
  const june = isJuneCohort(cls);
  // Names for the two remark boxes. Class teacher = the pupil's assigned CT
  // (authoritative on the class); HOY = whoever authored the HOY comment.
  const nameByUid = new Map((users ?? []).map((u) => [u.uid, u.displayName]));
  const ctTeacher = teachers.find((t) => t.id === cls?.classTeacherId || t.classTeacherOf === classId);
  const classTeacherName = ctTeacher?.displayName ?? '';

  // All marks docs for this class/term/year
  const marksSnap = await getDocs(
    query(
      collection(db, COLLECTIONS.marks),
      where('year', '==', year),
      where('term', '==', term),
      where('classId', '==', classId),
    ),
  );
  const marksDocs = marksSnap.docs.map((d) => d.data() as MarksDocument);

  // Attendance for the term, computed from the DAILY register
  // (reportDailyAttendance) — the collection the register + parent portal
  // actually write. The old aggregated reportAttendance collection was never
  // populated for the live term, so the report showed no attendance. Daily
  // docs carry only a `date` (no year/term), so we scope by the term's date
  // range (from the Term Calendar, built-in fallback otherwise).
  const range = resolveTermRange(term, termCalendar ?? null);
  const dailySnap = await getDocs(
    query(collection(db, COLLECTIONS.dailyAttendance), where('classId', '==', classId)),
  );
  const dailyDocs = dailySnap.docs
    .map((d) => ({ id: d.id, ...(d.data() as object) }) as DailyAttendanceDoc)
    .filter((d) => !range || (d.date >= range.start && d.date <= range.end));
  const rosterIds = students.filter((s) => s.currentClass === classId).map((s) => s.id);
  const attSummary = computeClassAttendance(classId, dailyDocs, rosterIds);
  const attTotals: Record<string, { daysAbsent: number; daysPresent: number; possibleDays: number }> = {};
  const possibleDays = dailyDocs.length;
  for (const st of attSummary.students) {
    attTotals[st.studentId] = {
      daysAbsent: st.absent + st.sick, // SICK counts as absent (policy Aug 2026)
      daysPresent: st.present + st.late, // late still attended
      possibleDays,
    };
  }

  // Comments doc (class-teacher + HOD), unless caller supplied edits
  let comments = commentsDoc ?? null;
  if (!comments) {
    const cSnap = await getDoc(
      doc(db, COLLECTIONS.reportComments, `${year}-${term}-${classId}`),
    );
    comments = cSnap.exists()
      ? (cSnap.data() as ReportCommentsDocument)
      : null;
  }

  const subjName = (id: string) =>
    subjects.find((s) => s.id === id)?.name ?? id;
  const subjScheme = (id: string) =>
    schemeForSubject(subjects.find((s) => s.id === id));
  const teacherName = (id?: string) =>
    teachers.find((t) => t.id === id)?.displayName ?? '';

  const roster = students
    .filter((s) => s.currentClass === classId)
    .sort((a, b) => a.fullName.localeCompare(b.fullName));

  const assembled = roster.map((stu) => {
    const cEntry = comments?.entries?.[stu.id];
    const subjectRows: ReportSubjectRow[] = [];
    for (const md of marksDocs) {
      const entry = md.entries?.[stu.id];
      if (!entry) continue;
      // June cohort is in ITS Term 1 (CA-only, no exam) even though the data
      // lives under the school-global term. Grade from the CA (Term-1 rule)
      // and drop the exam — otherwise the exam-graded Term-2 rule leaves them
      // ungraded. January classes are unaffected.
      const d = deriveMark(
        june ? 'Term1' : term,
        entry.ca,
        june ? undefined : (entry.exam as MaybeMark | undefined),
        subjScheme(md.subjectId),
      );
      const examNum =
        d.exam === undefined || d.exam === null || d.exam === ''
          ? null
          : d.exam === 'ABS'
            ? 'ABS'
            : Number(d.exam);
      if (!subjectBelongsOnReport({
        takesSubject: (stu.subjects ?? []).includes(md.subjectId),
        comment: entry.comment,
      })) continue;

      subjectRows.push({
        name: subjName(md.subjectId),
        caMark: d.caMark,
        exam: examNum as number | 'ABS' | null,
        termMark: d.termMark,
        grade: d.reportGrade,
        comment: entry.comment ?? '',
        // Prefer the teacher STAMPED on the mark (frozen at save); fall
        // back to the student's live subjectTeachers only for un-stamped
        // (older/pre-backfill) entries. Keeps prior-term credit stable
        // across a mid-year Teacher Swap.
        teacher: teacherName(
          entry.teacherId ?? stu.subjectTeachers?.[md.subjectId],
        ),
        scheme: subjScheme(md.subjectId),
      });
    }
    subjectRows.sort((a, b) => a.name.localeCompare(b.name));

    const att = attTotals[stu.id];
    return {
      name: stu.fullName,
      className,
      admissionNumber: stu.admissionNumber,
      dateOfBirth: stu.dateOfBirth,
      year,
      term,
      juneCohort: june,
      subjects: subjectRows,
      classTeacherComment: cEntry?.classTeacherComment ?? '',
      hodComment: cEntry?.hodComment ?? '',
      classTeacherName,
      hodName: cEntry?.hodCommentMeta?.authorId
        ? (nameByUid.get(cEntry.hodCommentMeta.authorId) ?? '')
        : '',
      daysAbsent: att?.daysAbsent,
      daysPresent: att?.daysPresent,
      possibleDays: att?.possibleDays,
    };
  });

  // Second pass: per-subject class mean, on the SAME basis the academic page
  // grades on (exam in exam terms, term-mark otherwise; June is CA-only via
  // termMark). Backfilled onto every row so the redesigned page can draw each
  // pupil's mark against their class average. Keyed by subject display name —
  // a class never carries two distinct subjects under one name.
  const usesExamBasis = june ? false : termUsesExam(term);
  const meanBySubject = new Map<string, { sum: number; n: number }>();
  for (const stu of assembled) {
    for (const row of stu.subjects) {
      const mk = usesExamBasis
        ? typeof row.exam === 'number'
          ? row.exam
          : null
        : row.termMark;
      if (typeof mk === 'number') {
        const acc = meanBySubject.get(row.name) ?? { sum: 0, n: 0 };
        acc.sum += mk;
        acc.n += 1;
        meanBySubject.set(row.name, acc);
      }
    }
  }
  for (const stu of assembled) {
    for (const row of stu.subjects) {
      const acc = meanBySubject.get(row.name);
      if (acc && acc.n > 0) row.classAverage = Math.round(acc.sum / acc.n);
    }
  }

  return assembled;
}
